/**
 * Demo Mode Hook
 * Manages demo mode state and query limits
 */

import { useMemo } from 'react'
import { branding } from '@/config/branding'
import { useChatStore } from '@/store/chatStore'

export interface DemoModeState {
  isDemoMode: boolean
  queriesCount: number
  maxQueries: number
  isBlocked: boolean
  queriesRemaining: number
}

/**
 * Hook for managing demo mode state
 * Tracks number of queries sent in current session
 */
export function useDemoMode(): DemoModeState {
  const { messages } = useChatStore()

  const isDemoMode = branding.isDemo
  const maxQueries = 4 // First query = mock, queries 2-4 = real, then block

  // Count user messages (queries sent)
  const queriesCount = useMemo(() => {
    if (!isDemoMode) return 0
    return messages.filter((msg) => msg.role === 'user').length
  }, [messages, isDemoMode])

  const isBlocked = isDemoMode && queriesCount >= maxQueries
  const queriesRemaining = Math.max(0, maxQueries - queriesCount)

  return {
    isDemoMode,
    queriesCount,
    maxQueries,
    isBlocked,
    queriesRemaining,
  }
}
